<template>
  <StaffLayout section="Admin Help">
    <h2>Client Admin Help Notes</h2>
    <table class="table-auto border-collapse border border-slate-500">
      <thead>
        <tr>
          <th>Page</th>
          <th>Note</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="page in adminPageNotes.filter((p) => !p.isForTenantAdmin)"
          class="hover:bg-blue-200 cursor-pointer"
          @click="editNote(page.page, false)"
        >
          <td class="border border-slate-600 p-1">{{ page.page }}</td>
          <td class="border border-slate-600 richText p-1" v-html="page.note"></td>
        </tr>
      </tbody>
    </table>
    <h2>Tenan Admin Help Notes</h2>
    <table class="table-auto border-collapse border border-slate-500">
      <thead>
        <tr>
          <th>Page</th>
          <th>Note</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="page in adminPageNotes.filter((p) => p.isForTenantAdmin)"
          class="hover:bg-blue-200 cursor-pointer"
          @click="editNote(page.page, true)"
        >
          <td class="border border-slate-600 p-1">{{ page.page }}</td>
          <td class="border border-slate-600 richText p-1" v-html="page.note"></td>
        </tr>
      </tbody>
    </table>
  </StaffLayout>

  <EditPopup
    :entityName="editingPage + ' Page Note'"
    :isEditMode="true"
    :isVisible="isEditingAdminNote"
    @save="saveNote"
    @close="isEditingAdminNote = false"
  >
    <div v-if="isEditingAdminNote && Ckeditor && Editor" class="h-full richText">
      <component :is="Ckeditor" :editor="Editor" v-model="editedNote" />
    </div>
  </EditPopup>
</template>

<script setup lang="ts">
import { useAdminStore } from "../../store/adminStore";
import { AdminNoteDTO, SaveAdminNoteRequest } from "../../store/generatedFetchClient";
import { useCKEditor } from "../../composables/useCKEditor";

const adminStore = useAdminStore();

// Load CKEditor using composable
const { Ckeditor, Editor } = useCKEditor();

const isEditingAdminNote = ref(false);
const editingPage = ref("");
const isEditingForTenantNote = ref(false);
const editedNote = ref("");

const adminPageNotes = ref([] as AdminNoteDTO[]);
const clientAdminPageNames = [
  "Admin Home",
  "Users",
  "Reports",
  "Tests",
  "Alert",
  "Documents",
  "Emergency Contacts",
  "Respond",
  "Tenants",
];
const tenantAdminPageNames = ["Admin Home", "Users", "Reports"];

onMounted(async () => {
  await getAllNotes();
});

async function getAllNotes() {
  adminPageNotes.value = await Promise.all([
    ...clientAdminPageNames.map(async (p) => {
      return await adminStore.getAdminNote(p, false, null);
    }),
    ...tenantAdminPageNames.map(async (p) => {
      return await adminStore.getAdminNote(p, true, null);
    }),
  ]);
}

function editNote(page: string, isForTenantAdmin: boolean) {
  editedNote.value = adminPageNotes.value.find(
    (p) => p.page == page && p.isForTenantAdmin == isForTenantAdmin
  ).note;
  editingPage.value = page;
  isEditingForTenantNote.value = isForTenantAdmin;
  isEditingAdminNote.value = true;
}

async function saveNote() {
  await adminStore.saveAdminNote({
    page: editingPage.value,
    note: editedNote.value,
    isForTenantAdmin: isEditingForTenantNote.value,
  } as SaveAdminNoteRequest);

  getAllNotes();

  isEditingAdminNote.value = false;
}
</script>

<style scoped>
.richText h1 {
  display: block;
  font-size: 2em;
  margin-top: 0.67em;
  margin-bottom: 0.67em;
  margin-left: 0;
  margin-right: 0;
  font-weight: bold;
}
.richText h2 {
  display: block;
  font-size: 1.5em;
  margin-top: 0.83em;
  margin-bottom: 0.83em;
  margin-left: 0;
  margin-right: 0;
  font-weight: bold;
}
.richText h3 {
  display: block;
  font-size: 1.17em;
  margin-top: 1em;
  margin-bottom: 1em;
  margin-left: 0;
  margin-right: 0;
  font-weight: bold;
}
.richText h4 {
  display: block;
  margin-top: 1.33em;
  margin-bottom: 1.33em;
  margin-left: 0;
  margin-right: 0;
  font-weight: bold;
}
.richText h5 {
  display: block;
  font-size: 0.83em;
  margin-top: 1.67em;
  margin-bottom: 1.67em;
  margin-left: 0;
  margin-right: 0;
  font-weight: bold;
}
.richText h6 {
  display: block;
  font-size: 0.67em;
  margin-top: 2.33em;
  margin-bottom: 2.33em;
  margin-left: 0;
  margin-right: 0;
  font-weight: bold;
}

.richText {
  font-size: medium;
}

.richText :deep(a) {
  color: blue;
  text-decoration: underline;
  cursor: pointer;
}

.richText :deep(a:hover) {
  color: cornflowerblue;
}

.richText :deep(ul) {
  padding-left: 24px;
}

.richText :deep(ul > li) {
  font-weight: normal;
  list-style-type: circle !important;
}

.richText :deep(ol) {
  counter-reset: li;
  margin-left: 0;
  padding-left: 0;
}

.richText :deep(ol > li) {
  position: relative;
  margin: 0 0 2px 2em;
  padding: 0 8px;
  list-style: none;
}

.richText :deep(ol > li:before) {
  content: counter(li);
  counter-increment: li;
  position: absolute;
  top: 0px;
  left: -1em;
  box-sizing: border-box;
}

.richText :deep(h2) {
  font-size: x-large;
}

.richText :deep(h3) {
  font-size: larger;
}

.richText :deep(h4) {
  font-size: large;
}
</style>
